<template>
  <div class="w-full min-h-screen bg-gray-200">
    <div class="pt-3 pb-0.5 mx-5 mb-3 flex flex-row justify-between items-end">
      <div class="flex flex-col">
        <h1 class="">Admin Dashboard</h1>
        <div class="text-sm text-gray-600">
          At-a-glance view of key system metrics. Click any card for full details.
        </div>
      </div>
      <div class="text-xs text-gray-500 italic">
        Cards marked SAMPLE are placeholders; expense panels are live.
      </div>
    </div>

    <div class="px-5 w-full pb-10">
      <ExpenseMetricsRow />
      <ResponsivenessMetricsRow />
      <div v-for="group in groups" :key="group.title" class="mb-6">
        <div class="flex flex-row items-center mb-1 ml-2">
          <div class="text-lg font-poppins tracking-wide text-dark-primary">
            {{ group.title }}
          </div>
          <div class="ml-3 text-xs text-gray-500">{{ group.description }}</div>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-1">
          <MetricCard
            v-for="card in group.cards"
            :key="card.title"
            v-bind="card"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import MetricCard from './MetricCard.vue'
import ExpenseMetricsRow from './ExpenseMetricsRow.vue'
import ResponsivenessMetricsRow from './ResponsivenessMetricsRow.vue'
import { getAdminDashboardData } from '@/services/Company'

const store = useStore()
const liveData = ref({})

async function loadLiveData() {
  const token = store.state.auth.token
  if (!token) return
  try {
    liveData.value = await getAdminDashboardData(token)
  } catch (e) {
    // Non-fatal: cards with a `liveKey` stay on '—'
  }
}
loadLiveData()

const rawGroups = [
  {
    title: 'Exports',
    description: 'CSV export volume and per-user usage against limits.',
    cards: [
      {
        title: 'Companies Exported (24h)',
        value: '—',
        subtitle: 'Sum across all exports today',
        to: '/admin-dashboard/exports',
        liveKey: 'companies_exported_24h',
      },
      {
        title: 'Companies Exported (30d)',
        value: '—',
        subtitle: 'Total volume, last 30 days',
        to: '/admin-dashboard/exports',
        liveKey: 'companies_exported_30d',
      },
      {
        title: 'Active Exporters (30d)',
        value: '—',
        subtitle: 'Distinct users',
        to: '/admin-dashboard/exports',
        liveKey: 'active_exporters_30d',
      },
      {
        title: 'Approaching Limit',
        value: '—',
        subtitle: 'Users ≥ 80% of 30-day limit',
        valueTone: 'warn',
        borderTone: 'warn',
        to: '/admin-dashboard/exports',
        liveKey: 'exporters_approaching_limit',
      },
      {
        title: 'Largest Single Export (7d)',
        value: '—',
        subtitle: 'Last 7 days',
        to: '/admin-dashboard/exports',
        liveKey: 'largest_single_export_7d_count',
        liveSubtitleKey: 'largest_single_export_7d_user',
      },
    ],
  },
  {
    title: 'App Usage',
    description: 'Page visits and back-end activity volume.',
    cards: [
      {
        title: 'Active Users (7d)',
        value: '—',
        subtitle: 'Distinct users with page visits',
        to: '/admin-dashboard/app-usage',
        liveKey: 'active_users_7d_count',
      },
      {
        title: 'Page Visits (30d)',
        value: '—',
        subtitle: 'Total navigations logged',
        to: '/admin-dashboard/app-usage',
        liveKey: 'page_visits_30d_count',
      },
      {
        title: 'API Calls (24h)',
        value: '—',
        subtitle: 'Back-end requests, last 24 hours',
        to: '/admin-dashboard/app-usage',
        liveKey: 'api_calls_24h_count',
      },
      {
        title: 'Top Page (30d)',
        value: '—',
        subtitle: 'Most-visited page',
        to: '/admin-dashboard/app-usage',
        liveKey: 'top_page_30d_count',
        liveSubtitleKey: 'top_page_30d_name',
      },
      {
        title: 'Top User (30d)',
        value: '—',
        subtitle: 'User with most page visits',
        to: '/admin-dashboard/app-usage',
        liveKey: 'top_user_30d_count',
        liveSubtitleKey: 'top_user_30d_name',
      },
    ],
  },
  {
    title: 'System Jobs & Automation',
    description: 'Scheduled overnight job runs and queue health.',
    cards: [
      {
        title: 'Overnight Jobs Succeeded',
        value: '\u2014',
        subtitle: 'Logged in last 24 hours',
        to: '/admin-dashboard/job-runs',
        liveKey: 'overnight_jobs_count_24h',
      },
      {
        title: 'Avg Job Duration',
        value: '\u2014',
        subtitle: 'Last 7 days',
        to: '/admin-dashboard/job-runs',
        liveKey: 'avg_job_duration_str_7d',
      },
      {
        title: 'Jobs In Queue',
        value: 17,
        subtitle: 'Pending celery tasks',
        badge: 'live',
        badgeTone: 'info',
        to: '/admin-dashboard/job-runs',
      },
      {
        title: 'Failed Jobs (7d)',
        value: 23,
        subtitle: 'Across all schedules',
        trend: '12% lower than prev week',
        trendDirection: 'up',
        to: '/admin-dashboard/job-runs',
      },
      {
        title: 'Longest Running Job',
        value: '—',
        subtitle: 'Last 7 days',
        borderTone: 'warn',
        to: '/admin-dashboard/job-runs',
        liveKey: 'longest_job_str_7d',
        liveSubtitleKey: 'longest_job_script_name_7d',
      },
    ],
  },
  {
    title: 'Data Quality',
    description: 'Duplicates, missing fields, and cleanup backlog.',
    cards: [
      {
        title: 'Duplicates In Campaigns',
        value: '—',
        subtitle: 'Active companies in campaigns with a duplicate UUID',
        valueTone: 'warn',
        borderTone: 'warn',
        to: '/admin-dashboard/duplicates',
        liveKey: 'duplicates_in_campaigns_count',
      },
      {
        title: 'Duplicates Resolved (30d)',
        value: 412,
        valueTone: 'good',
        trend: '+6% vs prior 30d',
        trendDirection: 'up',
        to: '/admin-dashboard/duplicates',
      },
      {
        title: 'Companies Missing URL',
        value: '—',
        subtitle: 'Imported but not URL-enriched',
        to: '/admin-dashboard/duplicates',
        liveKey: 'companies_missing_url_count',
      },
      {
        title: 'Companies Missing ZoomInfo',
        value: 932,
        subtitle: 'Active campaigns only',
        to: '/admin-dashboard/duplicates',
      },
      {
        title: 'Unverified URLs',
        value: 286,
        subtitle: 'Awaiting manual vetting',
        to: '/admin-dashboard/duplicates',
      },
    ],
  },
  {
    title: 'Companies Growth',
    description: 'New companies entering the system.',
    cards: [
      {
        title: 'Added Today',
        value: '—',
        subtitle: 'Net change since yesterday',
        to: '/admin-dashboard/companies-added',
        liveKey: 'added_today',
      },
      {
        title: 'Added This Week',
        value: '—',
        subtitle: 'Net change since 7d ago',
        valueTone: 'good',
        to: '/admin-dashboard/companies-added',
        liveKey: 'added_this_week',
      },
      {
        title: 'Added Last 30 Days',
        value: '—',
        subtitle: 'Net change since 30d ago',
        valueTone: 'good',
        to: '/admin-dashboard/companies-added',
        liveKey: 'added_last_30_days',
      },
      {
        title: 'Total Companies',
        value: '—',
        subtitle: 'All-time in system',
        to: '/admin-dashboard/companies-added',
        liveKey: 'total_companies',
      },
      {
        title: 'Added by Import',
        value: 724,
        subtitle: 'Last 30 days',
        badge: '56%',
        badgeTone: 'info',
        to: '/admin-dashboard/companies-added',
      },
    ],
  },
  {
    title: 'Campaigns & CRM',
    description: 'Pipeline activity across active campaigns.',
    cards: [
      {
        title: 'Active Campaigns',
        value: 62,
        subtitle: 'Across 41 clients',
        to: '#',
      },
      {
        title: 'Awaiting Client Review',
        value: 318,
        subtitle: 'Companies in Review status',
        badge: 'stale: 22',
        badgeTone: 'warn',
        to: '#',
      },
      {
        title: 'CRM Touches This Week',
        value: 1472,
        trend: '+3% vs prior week',
        trendDirection: 'up',
        to: '#',
      },
      {
        title: 'NDAs Out This Month',
        value: 58,
        to: '#',
      },
      {
        title: 'Quick Action Backlog',
        value: 204,
        subtitle: 'Across all CRM statuses',
        borderTone: 'warn',
        to: '#',
      },
    ],
  },
  {
    title: 'Users & Activity',
    description: 'Team engagement and research output.',
    cards: [
      {
        title: 'Active Users Today',
        value: 18,
        denominator: 34,
        subtitle: 'Internal users',
        to: '#',
      },
      {
        title: 'Active Users This Week',
        value: 29,
        denominator: 34,
        to: '#',
      },
      {
        title: 'Research Sessions Today',
        value: 47,
        trend: '+9 vs 7d avg',
        trendDirection: 'up',
        to: '#',
      },
      {
        title: 'Open Tasks',
        value: 523,
        subtitle: 'Across all users',
        to: '#',
      },
      {
        title: 'Overdue Tasks',
        value: 41,
        borderTone: 'bad',
        valueTone: 'bad',
        to: '#',
      },
    ],
  },
  {
    title: 'Accounting & Billing',
    description: 'Invoices, receipts, and Wave sync health.',
    cards: [
      {
        title: 'Open Invoices',
        value: 89,
        subtitle: 'Unpaid',
        to: '#',
      },
      {
        title: 'Invoices Overdue',
        value: 27,
        borderTone: 'bad',
        valueTone: 'bad',
        trend: '+4 this week',
        trendDirection: 'down',
        to: '#',
      },
      {
        title: 'Receipts This Month',
        value: '$284,310',
        valueTone: 'good',
        trend: '+12% vs last month',
        trendDirection: 'up',
        to: '#',
      },
      {
        title: 'Wave Sync Errors (7d)',
        value: 3,
        badge: 'review',
        badgeTone: 'warn',
        borderTone: 'warn',
        to: '#',
      },
      {
        title: 'Contingency Payments Due',
        value: 12,
        subtitle: 'In next 30 days',
        to: '#',
      },
    ],
  },
]

const groups = computed(() =>
  rawGroups.map(g => ({
    ...g,
    cards: g.cards.map(c => {
      if (c.liveKey && liveData.value[c.liveKey] != null) {
        const { liveKey, liveSubtitleKey, ...rest } = c
        const out = { ...rest, fakeData: false, value: liveData.value[c.liveKey] }
        if (liveSubtitleKey && liveData.value[liveSubtitleKey] != null) {
          out.subtitle = liveData.value[liveSubtitleKey]
        }
        return out
      }
      const { liveKey, liveSubtitleKey, ...rest } = c
      return { fakeData: true, ...rest }
    }),
  }))
)
</script>